import { Feature } from 'src/replicant/analytics';
import PopupBasicLazy from 'src/game/components/popups/PopupBasicLazy';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import View from '@play-co/timestep-core/lib/ui/View';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import { AssetGroup } from '@play-co/timestep-core';
import bitmapFonts from 'src/lib/bitmapFonts';
import gsap from 'gsap';
import { createUIPet } from 'src/game/pet/pet.utils';
import { waitForItPromise, withLoading } from 'src/lib/utils';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { petSpeciesAssets } from 'src/loadingGroups';
import {
  petAnimationNames,
  type PetAnimation,
} from 'src/replicant/pet/PetAssets';
import type { PetEntity } from 'src/game/logic/EntityManager';
import type {
  JankenMove,
  JankenOutcome,
} from 'src/replicant/janken/janken.ruleset';
import type { PetSpeciesID } from 'src/replicant/pet';
import { attributeBounds } from 'src/replicant/pet/pet.ruleset';
import NakedPromise from 'src/game/minigame/match2-timestep/lib/pattern/NakedPromise';
import JankenCareButton from '../components/JankenCareButton';
import JankenShareButtons from '../components/JankenShareButtons';
import type { JankenChallengeType } from '../janken.share';
import StateObserver, { locale, now, user } from 'src/StateObserver';
import { track } from 'src/lib/analytics/utils';
import { deriveJankenSenderMove } from 'src/replicant/janken/janken.utils';
import createIntervalEmitter, {
  IntervalEmitter,
} from 'src/lib/createIntervalEmitter';
import { formatDuration } from 'src/replicant/utils/time';

import sounds from 'src/lib/sounds';
import { downscale } from 'src/conf/scale';
import { SpineView, spineLoader } from '@play-co/timestep-spine';
import { playSpineAnimation } from 'src/lib/ui/utils/spine';
import type { AdultAnimationNames } from 'src/replicant/pet/PetAssets';

/*
 * ==========================================================================
 * PopupJanken.ts — Multi-mode janken popup (challenge, result, care).
 *
 * MODES (dispatched by init)
 *   'challenge'  → initIntro       — Single pet, START → countdown → share
 *   'result'     → initResult      — Two pets, countdown → reveal → outcome → CTA
 *   'care'       → initCare        — Two pets, ? marks, care CTA → transitions to result
 *
 * INIT & LIFECYCLE
 *   init(args)                     — Entry point, dispatches to mode-specific setup
 *   getAssetsGroup()               — Spine + image assets for lazy loading
 *   fadeOut()                      — Cleanup: nulls promises, removes pets/spines
 *
 * INTRO MODE (sender challenge)
 *   initIntro(args)                — Setup single pet + START button
 *   transitionIntroToChallenge()   — Countdown → reveal sender's move → share CTA
 *
 * RESULT MODE (receiver reveal)
 *   initResult(args)               — Setup two pets + timer, branch on skipAnimation
 *   initResultAnimated(args)       — START button → full animation sequence
 *   initResultSkipped(args)        — Show result immediately (no countdown)
 *   initResultSpines(args)         — Create spine entrance animation
 *   transitionResultToChallenge()  — Stop timer → run animateResult
 *
 * CARE MODE (pet needs attention)
 *   initCare(args)                 — Two pets + ? spines + care button + timer
 *   initCareSpines()               — Create spinning question mark spines
 *   awaitCareTap()                 — Promise resolved when care button tapped
 *   switchToResult(args)           — In-place transition: kill care → animateResult
 *
 * ANIMATION SEQUENCES
 *   animateResult(move, opp, outcome, name, hasReward)
 *     — Full sequence: load assets → countdown → smoke → reveal → outcome → CTA
 *   playSpineCountdown(sides)      — Red/both play animation + countdown overlay
 *   playCountdownOverlay()         — 3/2/1 number image pop sequence
 *   applyJankenSpineResult(...)    — Skins → red/blue_result → win/lose → idle loop
 *   applyOutcomeAnimations(outcome)— Pet reactions (joy/cry/angry)
 *   showOutcomeImage(outcome)      — Pop-in → hold → pop-out outcome badge
 *
 * ANIMATION HELPERS
 *   setPetAnimation(pet, anim)     — Set pet spine animation
 *   setPetWalkAnimation(pet)       — Set pet walking animation
 *   createJankenSpines()           — Create red + blue spine views
 *
 * UI HELPERS
 *   applyCtaConfig(config)         — Configure CTA button layout/text
 *   showNameLabels(opponentName)   — Show both pet name labels
 *   startTimer(expiresAt)          — Start countdown interval
 *   updateTimerText()              — Refresh timer display
 *   stopTimer()                    — Stop countdown interval
 *
 * CLEANUP
 *   cleanupPets()                  — Kill animations, stop timer, remove all views
 *   killAnimations()               — Kill GSAP tweens + spine tracks
 *
 * FLOW DIAGRAMS
 *
 *   Challenge mode (sender):
 *     init('challenge') → initIntro
 *       → user taps START
 *       → transitionIntroToChallenge
 *         → playSpineCountdown('blue') ∥ playCountdownOverlay
 *         → applyJankenSpineResult (own move shown)
 *         → share CTA
 *
 *   Result mode (receiver, animated):
 *     init('result') → initResult → initResultAnimated → initResultSpines
 *       → user taps START
 *       → transitionResultToChallenge → animateResult
 *         → pets walk to center + smoke start
 *         → playSpineCountdown('both') ∥ playCountdownOverlay
 *         → smoke end ∥ pets walk back ∥ applyJankenSpineResult
 *         → applyOutcomeAnimations + showOutcomeImage
 *         → CTA fade in (unless hasReward)
 *
 *   Result mode (receiver, skipped):
 *     init('result') → initResult → initResultSkipped
 *       → createJankenSpines → set result skins → applyOutcomeAnimations
 *       → showOutcomeImage → awaitResultComplete resolves
 *
 *   Care → Result transition:
 *     init('care') → initCare → initCareSpines
 *       → awaitCareTap (external caller resolves)
 *       → switchToResult → killAnimations → animateResult (same as above)
 * ==========================================================================
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Close action returned by PopupJanken:
 * - `'share'`         — user tapped CTA after choosing a move (sender flow)
 * - `'challengeBack'` — user tapped "Challenge Back" after losing
 * - `'rematch'`       — user tapped "Rematch" after a draw
 * - `'claim'`         — user tapped "Claim" after winning with a reward
 * - `'closed'`        — user pressed (x) after seeing the result
 * - `'dismissed'`     — user pressed (x) before seeing the result (e.g. during
 *                       care gate or intro); server action should be skipped
 */
export type PopupJankenClose =
  | 'share'
  | 'challengeBack'
  | 'dismissed'
  | 'closed'
  | 'claim'
  | 'rematch';

export type PopupJankenChallengeArgs = {
  mode: 'challenge';
  challengeId: string;
  challengeType: JankenChallengeType;
  myName: string;
  mySpecies: PetSpeciesID | undefined;
  myMove: JankenMove;
  expiresAt: number;
};

export type PopupJankenResultArgs = {
  mode: 'result';
  myOutcome: JankenOutcome;
  myMove: JankenMove;
  opponentMove: JankenMove;
  hasReward: boolean;
  myName: string;
  mySpecies: PetSpeciesID | undefined;
  opponentName: string;
  opponentSpecies: PetSpeciesID;
  expiresAt: number;
  skipAnimation: boolean;
};

export type PopupJankenCareArgs = {
  mode: 'care';
  myName: string;
  mySpecies: PetSpeciesID | undefined;
  opponentName: string;
  opponentSpecies: PetSpeciesID;
  expiresAt: number;
};

export type PopupJankenArgs =
  | PopupJankenChallengeArgs
  | PopupJankenResultArgs
  | PopupJankenCareArgs;

// ---------------------------------------------------------------------------
// Module-level instance access
// ---------------------------------------------------------------------------

let instance: PopupJanken | null = null;

export function getJankenPopupInstance(): PopupJanken | null {
  return instance;
}

// ---------------------------------------------------------------------------
// Asset paths
// ---------------------------------------------------------------------------

const JANKEN_ASSETS = {
  bg: 'assets/ui/janken/popup/bg.png',
  titleEn: 'assets/ui/janken/popup/title_en.png',
  titleJa: 'assets/ui/janken/popup/title_ja.png',
  frameRed: 'assets/ui/janken/icons/result_frame_red.png',
  frameBlue: 'assets/ui/janken/icons/result_frame_blue.png',
  questionRed: 'assets/ui/janken/icons/question_red.png',
  questionBlue: 'assets/ui/janken/icons/question_blue.png',
  rockLeft: 'assets/ui/janken/icons/rock_left.png',
  rockRight: 'assets/ui/janken/icons/rock_right.png',
  paperLeft: 'assets/ui/janken/icons/paper_left.png',
  paperRight: 'assets/ui/janken/icons/paper_right.png',
  scissorsLeft: 'assets/ui/janken/icons/scissors_left.png',
  scissorsRight: 'assets/ui/janken/icons/scissors_right.png',
  rockIcon: 'assets/ui/janken/icons/rock.png',
  num3: 'assets/ui/janken/numbers/3.png',
  num2: 'assets/ui/janken/numbers/2.png',
  num1: 'assets/ui/janken/numbers/1.png',
  nameLabelBlue: 'assets/ui/janken/popup/name_label_blue.png',
  nameLabelPink: 'assets/ui/janken/popup/name_label_pink.png',
} as const;

// ---------------------------------------------------------------------------
// Spine asset paths
// ---------------------------------------------------------------------------

const JANKEN_SPINE = 'assets/ui/janken/animation/spine.janken';
const JANKEN_EFFECT_SPINE = 'assets/ui/janken/animation/spine.janken_effect';
const JANKEN_SPINE_SCALE = 0.58;

// ---------------------------------------------------------------------------
// Spine animation timing (ms) — edit here to tune all transition speeds
// ---------------------------------------------------------------------------

const JANKEN_COUNTDOWN_TIMING = {
  // 3/2/1 number overlay: [image, delayBefore, holdDuration]
  numbers: [
    [JANKEN_ASSETS.num3, 400, 195],
    [JANKEN_ASSETS.num2, 170, 169],
    [JANKEN_ASSETS.num1, 200, 700],
  ] as [string, number, number][],
  // How long the spine play animation (red_play/blue_play) runs before switching to result.
  // Independent from the countdown overlay — both run in parallel.
  playDuration: 1700,
  // How many SFX ticks to play during the countdown (evenly spaced over playDuration).
  sfxCount: 18,
  sfxVolume: 0.8,
};

const JANKEN_RESULT_TIMING = {
  // How long to wait for the sign reveal animation (red_result / blue_result)
  resultReveal: 200,
  // How long to wait for the win/lose animation (red_result_win/lose etc.)
  winLose: 1500,
  // Delay (ms) from start of result reveal until post-result UI sequence begins.
  // Independent from the animation chain — single knob to tune.
  postResultDelay: 800,
  // How long the outcome image (win/lose/draw) stays visible before hiding.
  outcomeImageHold: 800,
  // Delay (ms) from start of post-result sequence until CTA fades in.
  // Independent from outcome image — single knob to tune CTA appearance.
  ctaRevealDelay: 200,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MOVE_I18N: Record<JankenMove, string> = {
  rock: 'janken.rock',
  paper: 'janken.paper',
  scissors: 'janken.scissors',
};

const OUTCOME_NOTIFICATION_KEY: Record<JankenOutcome, string> = {
  win: 'janken.notificationWin',
  lose: 'janken.notificationLose',
  draw: 'janken.notificationDraw',
};

const OUTCOME_SFX = {
  win: 'jankenWin',
  lose: 'jankenLose',
  draw: 'jankenDraw',
} as const satisfies Record<JankenOutcome, string>;

const OUTCOME_IMAGES: Record<
  JankenOutcome,
  Record<'en' | 'ja', { image: string; width: number; height: number }>
> = {
  win: {
    en: {
      image: 'assets/ui/janken/results/en/win.png',
      width: 345,
      height: 180,
    },
    ja: {
      image: 'assets/ui/janken/results/ja/win.png',
      width: 462,
      height: 218,
    },
  },
  lose: {
    en: {
      image: 'assets/ui/janken/results/en/lose.png',
      width: 675 * 0.9,
      height: 185 * 0.9,
    },
    ja: {
      image: 'assets/ui/janken/results/ja/lose.png',
      width: 461,
      height: 218,
    },
  },
  draw: {
    en: {
      image: 'assets/ui/janken/results/en/draw.png',
      width: 450,
      height: 185,
    },
    ja: {
      image: 'assets/ui/janken/results/ja/draw.png',
      width: 510,
      height: 218,
    },
  },
};

function getResultCloseAction(
  outcome: JankenOutcome,
  hasReward: boolean,
): PopupJankenClose {
  if (outcome === 'win' && hasReward) return 'claim';
  if (outcome === 'draw') return 'rematch';
  return 'challengeBack';
}

type CtaConfig = {
  width: { en: number; ja: number };
  textKey: string;
  fontSize: { en: number; ja: number };
  iconVisible: boolean;
};

const REVEAL_CTA: CtaConfig = {
  width: { en: 520, ja: 520 },
  textKey: 'janken.reveal',
  fontSize: { en: 52, ja: 48 },
  iconVisible: true,
};

const CLAIM_REWARD_CTA: CtaConfig = {
  width: { en: 520, ja: 520 },
  textKey: 'janken.claimReward',
  fontSize: { en: 52, ja: 48 },
  iconVisible: false,
};

const REMATCH_CTA: CtaConfig = {
  width: { en: 430, ja: 430 },
  textKey: 'janken.rematch',
  fontSize: { en: 52, ja: 48 },
  iconVisible: true,
};

const CHALLENGE_BACK_CTA: CtaConfig = {
  width: { en: 560, ja: 360 },
  textKey: 'janken.challengeBack',
  fontSize: { en: 44, ja: 44 },
  iconVisible: true,
};

function getResultCtaConfig(
  outcome: JankenOutcome,
  hasReward: boolean,
): CtaConfig {
  if (outcome === 'win' && hasReward) return CLAIM_REWARD_CTA;
  if (outcome === 'draw') return REMATCH_CTA;
  return CHALLENGE_BACK_CTA;
}

function pickRandom<T>(items: T[]): T {
  return items[Math.floor(Math.random() * items.length)];
}

function createNameLabel(
  superview: View,
  x: number,
  image: string,
  localeText: () => string,
): [ImageScaleView, LangBitmapFontTextView] {
  const label = new ImageScaleView({
    superview,
    x,
    y: 584,
    width: downscale(282),
    height: downscale(86),
    image,
    visible: false,
  });
  const text = new LangBitmapFontTextView({
    superview: label,
    x: 9,
    y: 8,
    width: 170,
    height: 36,
    align: 'center',
    verticalAlign: 'center',
    size: 26,
    color: '#FFFFFF',
    font: bitmapFonts('Body'),
    canHandleEvents: false,
    localeText,
  });
  return [label, text];
}

// ---------------------------------------------------------------------------
// Popup
// ---------------------------------------------------------------------------

export default class PopupJanken extends PopupBasicLazy {
  // -- Outcome image (locale-specific win/lose/draw) --
  private outcomeImage!: ImageScaleView;

  // -- Instruction area --
  private instructionBg!: View;
  private instructionText!: LangBitmapFontTextView;

  // -- Timer --
  private timerContainer!: View;
  private timerText!: LangBitmapFontTextView;
  private timerInterval: IntervalEmitter<{ now: number }> | null = null;
  private expiresAt = 0;

  // -- CTA button --
  private ctaButton!: ButtonScaleViewWithText;
  private ctaIcon!: ImageScaleView;
  private countdownImage!: ImageScaleView;

  // -- Care button --
  private careButton!: JankenCareButton;

  // -- Pets --
  private myPet: PetEntity | null = null;
  private opponentPet: PetEntity | null = null;

  // -- Spine animations --
  private jankenSpineRed: SpineView | null = null;
  private jankenSpineBlue: SpineView | null = null;
  private smokeSpine: SpineView | null = null;

  // -- Name labels --
  private myNameLabel!: ImageScaleView;
  private myNameText!: LangBitmapFontTextView;
  private opponentNameLabel!: ImageScaleView;
  private opponentNameText!: LangBitmapFontTextView;

  // -- State --
  private currentCloseAction: PopupJankenClose = 'dismissed';

  /** Returns `'closed'` if the user has seen the result, `'dismissed'` otherwise. */
  private getCloseAction(): PopupJankenClose {
    const a = this.currentCloseAction;
    return a === 'claim' || a === 'rematch' || a === 'challengeBack'
      ? 'closed'
      : 'dismissed';
  }
  private mySpecies: PetSpeciesID = 'dog';
  private challengeType: JankenChallengeType = 'challenge';
  private opponentSpecies: PetSpeciesID = 'dog';
  private careTapPromise: NakedPromise | null = null;
  private resultCompletePromise: NakedPromise | null = null;
  private inCareMode = false;
  private onCtaClick: (() => void) | null = null;

  // -- Share buttons (LINE + Others, shown on LINE Mini App) --
  private shareButtons!: JankenShareButtons;

  // -- Re-roll button (challenge mode only) --
  private rerollButton!: ButtonScaleView;

  constructor(opts: {
    superview: View;
    close: (result: PopupJankenClose) => void;
  }) {
    super({
      superview: opts.superview,
      close: () => opts.close(this.getCloseAction()),
      width: 680,
      height: 920,
      skipTitle: true,
      skipBackground: true,
      skipMessage: true,
      darkerBg: true,
      tracking: {
        feature: Feature.JANKEN._,
        $subFeature: Feature.JANKEN.POPUP_MAIN,
      },
    });

    instance = this;

    // -- Custom background --
    new ImageScaleView({
      superview: this.box,
      x: 0,
      y: 0,
      width: 680,
      height: 920,
      image: JANKEN_ASSETS.bg,
      canHandleEvents: false,
    });

    // -- Localized title image --
    const locale = StateObserver.getLocale();
    const titleW = locale === 'ja' ? 508 : 558;
    const titleH = locale === 'ja' ? 188 : 184;
    new ImageScaleView({
      superview: this.box,
      x: (680 - titleW) / 2,
      y: 15,
      width: titleW,
      height: titleH,
      image: locale === 'ja' ? JANKEN_ASSETS.titleJa : JANKEN_ASSETS.titleEn,
    });

    // -- Info button (overlaps lower-left of title, per Figma) --
    new ButtonScaleView({
      superview: this.box,
      ...uiConfig.buttons.info,
      x: uiConfig.buttons.info.width / 2 - 10,
      y: 121,
      onClick: async () => {
        sounds.playSound('ok');
        openPopupPromise('popupJankenInfo', {});
      },
      tracking: { feature: Feature.JANKEN._, $subFeature: Feature.JANKEN.INFO },
    });

    // -- Standard shared close button (top-right, aligned with info button) --
    // y matches the info button's center.
    this.buttonClose.updateOpts({
      x: this.box.style.width - this.buttonClose.style.width / 2 + 10,
      y: 121,
    });
    // Preserve the original janken_close analytics attribution.
    this.buttonClose.tracking = {
      feature: Feature.JANKEN._,
      $subFeature: Feature.JANKEN.CLOSE,
    };

    // -- Outcome image (locale-specific win/lose/draw) --
    this.outcomeImage = new ImageScaleView({
      superview: this.box,
      canHandleEvents: false,
      visible: false,
      zIndex: 20,
    });

    // -- Instruction background --
    this.instructionBg = new View({
      superview: this.box,
      x: 22,
      y: 655,
      width: 636,
      height: 65,
      backgroundColor: '#3B5098',
      opacity: 0.4,
    });

    // -- Instruction text --
    this.instructionText = new LangBitmapFontTextView({
      superview: this.box,
      x: 45,
      y: 673,
      width: 590,
      height: 31,
      align: 'center',
      verticalAlign: 'center',
      size: 26,
      color: '#FFFFFF',
      font: bitmapFonts('Title'),
      canHandleEvents: false,
    });

    // -- Timer (time_frame.png 3-slice, same pattern as RacesTimer) --
    this.timerContainer = new View({
      superview: this.box,
      x: 239,
      y: 15 + titleH - 10,
      width: 202,
      height: 52,
    });

    new ImageScaleView({
      superview: this.timerContainer,
      width: 202,
      height: 52,
      image: 'assets/ui/match2/races/common/time_frame.png',
      scaleMethod: '3slice',
      sourceSlices: {
        horizontal: { left: 48, right: 24 },
      },
    });

    this.timerText = new LangBitmapFontTextView({
      superview: this.timerContainer,
      x: 110,
      y: 23,
      centerOnOrigin: true,
      align: 'center',
      verticalAlign: 'center',
      size: 24,
      color: '#BF8147',
      font: bitmapFonts('Body'),
      width: 110,
      height: 30,
      canHandleEvents: false,
    });

    // -- CTA button --
    this.ctaButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      x: 340,
      y: 797,
      width: 560,
      height: 104,
      centerOnOrigin: true,
      labelOffsetX: -70,
      labelOffsetY: -1,
      fontSize: 52,
      font: 'Body',
      onClick: async () => {
        if (this.onCtaClick) {
          this.onCtaClick();
        } else {
          opts.close(this.currentCloseAction);
        }
      },
      tracking: {
        feature: Feature.JANKEN._,
        $subFeature: Feature.JANKEN.POPUP_CTA,
      },
    });

    // -- CTA icon (rock icon inside button, sized per mode) --
    this.ctaIcon = new ImageScaleView({
      superview: this.ctaButton,
      image: JANKEN_ASSETS.rockIcon,
      width: 90,
      height: 90,
      x: -120,
      y: -45,
      canHandleEvents: false,
    });

    // -- Share buttons (LINE + Others — visible on LINE Mini App) --
    this.shareButtons = new JankenShareButtons({
      superview: this.box,
      x: 340,
      y: 797,
      width: this.box.style.width,
    });

    // -- Re-roll button (challenge mode: to the right of blue outcome circle) --
    this.rerollButton = new ButtonScaleView({
      superview: this.box,
      x: 465,
      y: 250,
      width: 61,
      height: 61,
      zIndex: 20,
      visible: false,
      pressedOffsetY: 3,
      icon: {
        image: 'assets/ui/janken/icons/reroll.png',
        width: 61,
        height: 61,
      },
      onClick: async () => {
        sounds.playSound('ok');
        await this.reRollChallenge();
      },
      tracking: {
        feature: Feature.JANKEN._,
        $subFeature: Feature.JANKEN.REROLL,
      },
    });

    // -- Countdown number image (shown at CTA position during countdown) --
    this.countdownImage = new ImageScaleView({
      superview: this.box,
      x: 340,
      y: 731,
      width: 259,
      height: 203,
      centerOnOrigin: true,
      visible: false,
      canHandleEvents: false,
    });

    // -- Care button --
    this.careButton = this.box.addSubview(
      new JankenCareButton({
        onClick: () => {
          sounds.playSound('ok');
          if (this.careTapPromise) {
            this.careTapPromise.resolve();
            this.careTapPromise = null;
          } else if (!this.inCareMode) {
            opts.close(this.currentCloseAction);
          }
        },
      }),
    );
    this.careButton.updateOpts({
      x: 53,
      y: 745,
      visible: false,
    });

    // -- Name labels (below pets, visible in care + result modes) --
    const [myLabel, myText] = createNameLabel(
      this.box,
      80,
      JANKEN_ASSETS.nameLabelBlue,
      () => i18n('janken.you'),
    );
    this.myNameLabel = myLabel;
    this.myNameText = myText;

    const [oppLabel, oppText] = createNameLabel(
      this.box,
      380,
      JANKEN_ASSETS.nameLabelPink,
      () => '',
    );
    this.opponentNameLabel = oppLabel;
    this.opponentNameText = oppText;
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      ...AssetGroup.constructURLs([
        'assets/ui/janken/popup',
        'assets/ui/janken/icons',
        'assets/ui/janken/numbers',
        'assets/ui/janken/results/en',
        'assets/ui/janken/results/ja',
        'assets/sounds/sfx/janken',
      ]),
      { url: JANKEN_SPINE, loadMethod: spineLoader },
      { url: JANKEN_EFFECT_SPINE, loadMethod: spineLoader },
      {
        url: 'assets/ui/janken/animation/spine.confetti',
        loadMethod: spineLoader,
      },
      {
        url: 'assets/ui/janken/animation/spine.radial',
        loadMethod: spineLoader,
      },
    ]);
  }

  // ---------------------------------------------------------------------------
  // Init — dispatches to mode-specific setup
  // ---------------------------------------------------------------------------

  public init(args: PopupJankenArgs) {
    super.init({});
    this.cleanupPets();

    if (args.mode === 'challenge') {
      this.initIntro(args);
    } else if (args.mode === 'care') {
      this.initCare(args);
    } else {
      this.initResult(args);
    }
  }

  // ---------------------------------------------------------------------------
  // Intro state — START button, single pet, no circles
  // ---------------------------------------------------------------------------

  private initIntro(args: PopupJankenChallengeArgs) {
    this.currentCloseAction = 'dismissed';
    this.mySpecies = args.mySpecies ?? 'dog';
    this.challengeType = args.challengeType;
    // Hide outcome text, care button, name labels, re-roll button
    this.outcomeImage.updateOpts({ visible: false });
    this.careButton.updateOpts({ visible: false });
    this.rerollButton.updateOpts({ visible: false });
    this.myNameLabel.updateOpts({ visible: false });
    this.opponentNameLabel.updateOpts({ visible: false });

    // Timer hidden for challenge intro (single pet screen)
    this.timerContainer.updateOpts({ visible: false });

    // Instruction text
    this.instructionBg.updateOpts({ visible: true, opacity: 0.7 });
    this.instructionText.updateOpts({ visible: true });
    this.instructionText.localeText = () => i18n('janken.introInstruction');

    // CTA — narrow START button with rock icon
    this.ctaButton.updateOpts({ width: 374, opacity: 1, centerOnOrigin: true });
    this.ctaButton.localeText = () => i18n('janken.start');
    this.ctaIcon.updateOpts({
      visible: true,
      width: 90,
      height: 90,
      offsetX: 15,
      offsetY: 5,
      x: 0,
      y: 0,
    });
    this.onCtaClick = () => void this.transitionIntroToChallenge(args);

    // Single pet — centered
    this.myPet = createUIPet('happy', this.mySpecies);
    this.myPet.updateOpts({
      superview: this.box,
      x: 340,
      y: 455,
      scale: 0.85,
    });

    // Spine "?" circle above pet — shows no result yet
    void spineLoader(JANKEN_SPINE).then(() => {
      if (!this.myPet) return; // popup already disposed
      this.jankenSpineBlue = new SpineView({
        superview: this.box,
        spinePath: JANKEN_SPINE,
        x: 420,
        y: 287,
        zIndex: 15,
        scale: JANKEN_SPINE_SCALE,
      });
      this.jankenSpineBlue.state.setAnimation(0, 'blue_init', false);
      this.jankenSpineBlue.state.addAnimation(0, 'blue_idle', true, 0);
    });
  }

  private async transitionIntroToChallenge(args: PopupJankenChallengeArgs) {
    sounds.playSound('ok');

    // Disable CTA during animation
    this.ctaButton.updateOpts({ opacity: 0 });
    this.onCtaClick = null;

    // Update instruction text immediately so it's visible during loading
    this.instructionText.localeText = () =>
      i18n('janken.challengeInstruction', {
        move: i18n(MOVE_I18N[args.myMove]),
      });

    // Load pet assets
    if (!this.myPet) return;
    await withLoading(() => petSpeciesAssets[this.mySpecies].load(), {
      superview: this.myPet,
      scale: 1.25,
      feature: 'pet',
      subFeature: 'pet_assets',
    });

    // Ensure spine is ready (cache hit from initIntro)
    await spineLoader(JANKEN_SPINE);
    if (!this.myPet) return;

    this.setPetAnimation(this.myPet, 'play');

    this.instructionBg.updateOpts({ visible: false });
    this.instructionText.updateOpts({ visible: false });

    await this.playSpineCountdown('blue');

    // Spine reveal and post-result UI run in parallel
    await Promise.all([
      this.applyJankenSpineResult(args.myMove, args.myMove, 'win', 'blue'),
      this.showPostChallengeUI({
        challengeId: args.challengeId,
        myMove: args.myMove,
      }),
    ]);
  }

  /** Re-roll: generate new challenge and replay countdown without closing popup. */
  private async reRollChallenge() {
    // Hide post-challenge UI
    this.rerollButton.updateOpts({ visible: false });
    this.shareButtons.reset();
    this.instructionBg.updateOpts({ visible: false });
    this.instructionText.updateOpts({ visible: false });
    this.currentCloseAction = 'dismissed';

    // Generate new challenge
    const challengeId = await StateObserver.invoke.generateJankenChallengeId();
    const myMove = deriveJankenSenderMove(challengeId, user().id);

    // Update instruction text for after reveal
    this.instructionText.localeText = () =>
      i18n('janken.challengeInstruction', {
        move: i18n(MOVE_I18N[myMove]),
      });

    // Replay countdown and reveal
    this.setPetAnimation(this.myPet, 'play');
    await this.playSpineCountdown('blue');

    await Promise.all([
      this.applyJankenSpineResult(myMove, myMove, 'win', 'blue'),
      this.showPostChallengeUI({ challengeId, myMove }),
    ]);

    track('JankenReroll', { challengeId, move: myMove });
  }

  /** Post-result UI: instruction text, pet reaction, share/re-roll buttons fade in. */
  private async showPostChallengeUI(opts: {
    challengeId: string;
    myMove: JankenMove;
  }) {
    await waitForItPromise(JANKEN_RESULT_TIMING.postResultDelay);

    // Fade in instruction banner
    this.instructionBg.updateOpts({ visible: true, opacity: 0, y: 675 });
    this.instructionText.updateOpts({ visible: true, opacity: 0, y: 693 });
    gsap.to(this.instructionBg.style, {
      opacity: 0.7,
      y: 655,
      duration: 0.35,
      ease: 'power2.out',
    });
    gsap.to(this.instructionText.style, {
      opacity: 1,
      y: 673,
      duration: 0.35,
      ease: 'power2.out',
    });

    // Pet reacts after countdown
    this.setPetAnimation(
      this.myPet,
      pickRandom<PetAnimation>(['surprised', 'waive', 'anxious']),
    );
    if (this.myPet) gsap.to(this.myPet.style, { x: 320, duration: 0.3 });

    // Show share buttons and re-roll button
    this.currentCloseAction = 'share';
    this.ctaButton.updateOpts({ visible: false });
    this.rerollButton.updateOpts({ visible: true, opacity: 0 });
    gsap.to(this.rerollButton.style, { opacity: 1, duration: 0.3 });
    await this.shareButtons.showForChallenge(
      opts.challengeId,
      opts.myMove,
      this.challengeType,
    );
  }

  // ---------------------------------------------------------------------------
  // Result mode — two pets, countdown, reveal, outcome, CTA
  // ---------------------------------------------------------------------------

  private applyCtaConfig(config: CtaConfig) {
    const isJa = locale() === 'ja';
    const btnWidth = isJa ? config.width.ja : config.width.en;
    const btnHeight = 114;
    const iconSize = 90;
    const iconX = 10;
    const iconY = btnHeight - iconSize - 15;
    const labelHeight = Math.round(btnHeight * 0.81);

    let labelX: number;
    let labelWidth: number;
    let labelAlign: 'left' | 'center';

    if (config.iconVisible) {
      labelX = iconX + iconSize + 8;
      labelWidth = Math.round((btnWidth - labelX - 20) * 0.95);
      labelAlign = 'left';
    } else {
      const padding = 20;
      labelWidth = Math.round((btnWidth - padding * 2) * 0.95);
      labelX = Math.round((btnWidth - labelWidth) / 2);
      labelAlign = 'center';
    }

    this.ctaButton.updateOpts({
      width: btnWidth,
      height: btnHeight,
      centerOnOrigin: true,
    });
    this.ctaButton.localeText = () =>
      i18n(config.textKey as Parameters<typeof i18n>[0]);
    this.ctaButton.label.updateOpts({
      size: isJa ? config.fontSize.ja : config.fontSize.en,
      width: labelWidth,
      height: labelHeight,
      align: labelAlign,
    });
    this.ctaButton.setLabelPosition({ x: labelX, y: 4 });
    this.ctaIcon.updateOpts({
      visible: config.iconVisible,
      width: iconSize,
      height: iconSize,
      x: iconX,
      y: iconY,
      offsetX: 0,
      offsetY: 0,
    });
  }

  private initResult(args: PopupJankenResultArgs) {
    this.currentCloseAction = 'dismissed';
    this.mySpecies = args.mySpecies ?? 'dog';
    this.opponentSpecies = args.opponentSpecies;

    // Hide care-only elements
    this.careButton.updateOpts({ visible: false });

    // Outcome image — hidden
    this.outcomeImage.updateOpts({ visible: false });

    // Timer
    this.startTimer(args.expiresAt);

    // Pets — same size/position as care mode
    this.myPet = createUIPet('happy', this.mySpecies);
    this.myPet.updateOpts({
      superview: this.box,
      x: 171,
      y: 481,
      scale: 0.85,
    });

    this.opponentPet = createUIPet('happy', this.opponentSpecies);
    this.opponentPet.setFlipped(true);
    this.opponentPet.updateOpts({
      superview: this.box,
      x: 471,
      y: 483,
      scale: 0.85,
    });

    // Load opponent pet assets with spinner — visible immediately, not just during animation
    void withLoading(() => petSpeciesAssets[this.opponentSpecies].load(), {
      superview: this.opponentPet,
      scale: 1.25,
      feature: 'pet',
      subFeature: 'pet_assets',
    });

    // Name labels
    this.showNameLabels(args.opponentName);

    if (args.skipAnimation) {
      this.initResultSkipped(args);
    } else {
      this.initResultAnimated(args);
    }
  }

  private initResultSkipped(args: PopupJankenResultArgs) {
    this.resultCompletePromise = new NakedPromise();
    this.instructionBg.updateOpts({ visible: true, opacity: 0.7 });
    this.instructionText.updateOpts({ visible: true });
    this.instructionText.localeText = () =>
      i18n(
        OUTCOME_NOTIFICATION_KEY[args.myOutcome] as Parameters<typeof i18n>[0],
        { name: args.opponentName },
      );

    this.currentCloseAction = getResultCloseAction(
      args.myOutcome,
      args.hasReward,
    );
    this.applyCtaConfig(getResultCtaConfig(args.myOutcome, args.hasReward));
    this.ctaButton.updateOpts({ opacity: 0 });
    this.onCtaClick = null;

    void this.showResultSkipped(args);
  }

  private initResultAnimated(args: PopupJankenResultArgs) {
    this.resultCompletePromise = new NakedPromise();
    this.instructionBg.updateOpts({ visible: true, opacity: 0.7 });
    this.instructionText.updateOpts({ visible: true });
    this.instructionText.localeText = () => i18n('janken.resultInstruction');

    this.applyCtaConfig(REVEAL_CTA);
    this.ctaButton.updateOpts({ opacity: 0 });
    this.onCtaClick = null;

    // Create spine circles with pop-up entrance, then idle loop while waiting
    void this.initResultSpines(args);
  }

  private async initResultSpines(args: PopupJankenResultArgs) {
    await this.createJankenSpines();
    if (!this.jankenSpineRed || !this.jankenSpineBlue) return;

    // Pop-up entrance → idle loop
    this.jankenSpineRed.state.setAnimation(0, 'red_init', false);
    this.jankenSpineRed.state.addAnimation(0, 'red_idle', true, 0);
    this.jankenSpineBlue.state.setAnimation(0, 'blue_init', false);
    this.jankenSpineBlue.state.addAnimation(0, 'blue_idle', true, 0);

    // Show CTA after spine circles are visible
    this.ctaButton.updateOpts({ opacity: 1 });
    this.onCtaClick = () => void this.transitionResultToChallenge(args);
  }

  private async transitionResultToChallenge(args: PopupJankenResultArgs) {
    sounds.playSound('ok');
    const { myMove, opponentMove, myOutcome, hasReward, opponentName } = args;

    // Hide CTA, timer, and instruction during animation
    this.ctaButton.updateOpts({ opacity: 0 });
    this.onCtaClick = null;
    this.stopTimer();
    this.timerContainer.updateOpts({ visible: false });
    this.instructionBg.updateOpts({ visible: false });
    this.instructionText.updateOpts({ visible: false });

    // Set close action for (x) button; CTA config is applied by postResultSequence
    this.currentCloseAction = getResultCloseAction(myOutcome, hasReward);

    // Run the full result animation (loads assets, countdown, outcome, CTA fade)
    await this.animateResult(
      myMove,
      opponentMove,
      myOutcome,
      opponentName,
      hasReward,
    );
  }

  private async animateResult(
    myMove: JankenMove,
    opponentMove: JankenMove,
    myOutcome: JankenOutcome,
    opponentName: string,
    hasReward: boolean,
  ) {
    await waitForItPromise(300);
    if (!this.opponentPet) return;
    await withLoading(() => petSpeciesAssets[this.opponentSpecies].load(), {
      superview: this.opponentPet,
      scale: 1.25,
      feature: 'pet',
      subFeature: 'pet_assets',
    });
    if (!this.opponentPet) return;

    // Spine circles: create if not already present (care→result transition),
    // otherwise reuse from initResultSpines (normal result flow)
    const needsSpineCreation = !this.jankenSpineRed;
    if (needsSpineCreation) {
      await this.createJankenSpines();
    }
    if (!this.jankenSpineRed || !this.jankenSpineBlue) return;
    if (needsSpineCreation) {
      this.jankenSpineRed.state.setAnimation(0, 'red_init', false);
      this.jankenSpineRed.state.addAnimation(0, 'red_idle', true, 0);
      this.jankenSpineBlue.state.setAnimation(0, 'blue_init', false);
      this.jankenSpineBlue.state.addAnimation(0, 'blue_idle', true, 0);
    }

    // Pets walk toward center (fire-and-forget — finishes well before countdown ends)
    this.setPetWalkAnimation(this.myPet);
    this.setPetWalkAnimation(this.opponentPet);
    if (this.myPet) void gsap.to(this.myPet.style, { x: 250, duration: 0.6 });
    if (this.opponentPet)
      void gsap.to(this.opponentPet.style, { x: 390, duration: 0.6 });

    // Smoke effect at center between the two pets
    this.smokeSpine = new SpineView({
      superview: this.box,
      spinePath: JANKEN_EFFECT_SPINE,
      x: 320,
      y: 460,
      zIndex: 8,
    });
    // Smoke and spine countdown start together (spines already hidden during pet walk)
    void playSpineAnimation(this.smokeSpine, { animation: 'smoke_start' }).then(
      () => {
        if (!this.smokeSpine) return;
        playSpineAnimation(this.smokeSpine, {
          animation: 'smoke_loop',
          loop: true,
        });
      },
    );
    await this.playSpineCountdown('both');

    // Smoke dispersal, result reveal, post-result UI, CTA — all in parallel
    await Promise.all([
      this.disperseSmokeAndReveal(myMove, opponentMove, myOutcome),
      this.showPostResultUI(myOutcome, opponentName, hasReward),
      this.revealResultCta(),
    ]);
    this.resultCompletePromise?.resolve();
  }

  /** Smoke dispersal + pets walk back + spine result reveal, then clean up smoke. */
  private async disperseSmokeAndReveal(
    myMove: JankenMove,
    opponentMove: JankenMove,
    myOutcome: JankenOutcome,
  ) {
    if (!this.smokeSpine) return;
    const smokeSpine = this.smokeSpine;
    await Promise.all([
      playSpineAnimation(smokeSpine, { animation: 'smoke_end', clear: true }),
      this.myPet
        ? gsap.to(this.myPet.style, { x: 171, duration: 0.5 })
        : Promise.resolve(),
      this.opponentPet
        ? gsap.to(this.opponentPet.style, { x: 471, duration: 0.5 })
        : Promise.resolve(),
      this.applyJankenSpineResult(myMove, opponentMove, myOutcome, 'both'),
    ]);
    smokeSpine.removeFromSuperview();
    this.smokeSpine = null;
  }

  /** Post-result UI: instruction banner, outcome image, pet animations. */
  private async showPostResultUI(
    myOutcome: JankenOutcome,
    opponentName: string,
    hasReward: boolean,
  ) {
    await waitForItPromise(JANKEN_RESULT_TIMING.postResultDelay);

    this.applyCtaConfig(getResultCtaConfig(myOutcome, hasReward));
    this.onCtaClick = null;

    this.instructionBg.updateOpts({ visible: true, opacity: 0, y: 675 });
    this.instructionText.updateOpts({ visible: true, opacity: 0, y: 693 });
    this.instructionText.localeText = () =>
      i18n(OUTCOME_NOTIFICATION_KEY[myOutcome] as Parameters<typeof i18n>[0], {
        name: opponentName,
      });
    gsap.to(this.instructionBg.style, {
      opacity: 0.7,
      y: 655,
      duration: 0.35,
      ease: 'power2.out',
    });
    gsap.to(this.instructionText.style, {
      opacity: 1,
      y: 673,
      duration: 0.35,
      ease: 'power2.out',
    });

    this.applyOutcomeAnimations(myOutcome);
    void this.showOutcomeImage(myOutcome);
  }

  /** Fade in CTA after result animation. */
  private async revealResultCta() {
    await waitForItPromise(
      JANKEN_RESULT_TIMING.postResultDelay +
        JANKEN_RESULT_TIMING.ctaRevealDelay,
    );
    await gsap.to(this.ctaButton.style, { opacity: 1, duration: 0.3 });
  }

  private async showResultSkipped(args: PopupJankenResultArgs) {
    if (!this.opponentPet) return;
    await withLoading(() => petSpeciesAssets[this.opponentSpecies].load(), {
      superview: this.opponentPet,
      scale: 1.25,
      feature: 'pet',
      subFeature: 'pet_assets',
    });

    // Spine circles — show final result state immediately (no countdown)
    await this.createJankenSpines();
    if (!this.jankenSpineBlue || !this.jankenSpineRed) return;
    const blueAnim =
      args.myOutcome === 'win' || args.myOutcome === 'draw'
        ? 'blue_result_win'
        : 'blue_result_lose';
    const redAnim =
      args.myOutcome === 'lose' || args.myOutcome === 'draw'
        ? 'red_result_win'
        : 'red_result_lose';
    this.jankenSpineBlue.skeleton.setSkinByName(args.myMove);
    this.jankenSpineRed.skeleton.setSkinByName(args.opponentMove);
    this.jankenSpineBlue.state.setAnimation(0, blueAnim + '_idle', true);
    this.jankenSpineRed.state.setAnimation(0, redAnim + '_idle', true);

    this.setPetAnimation(this.myPet, 'play');
    this.setPetAnimation(this.opponentPet, 'play');
    sounds.playSound(OUTCOME_SFX[args.myOutcome], 0.5);
    this.applyOutcomeAnimations(args.myOutcome);
    void this.showOutcomeImage(args.myOutcome);
    await waitForItPromise(JANKEN_RESULT_TIMING.ctaRevealDelay);
    await gsap.to(this.ctaButton.style, { opacity: 1, duration: 0.3 });
    this.resultCompletePromise?.resolve();
  }

  // ---------------------------------------------------------------------------
  // Animation helpers
  // ---------------------------------------------------------------------------

  private setPetAnimation(pet: PetEntity | null, anim: PetAnimation) {
    const species = pet === this.myPet ? this.mySpecies : this.opponentSpecies;
    pet?.setAnimation(petAnimationNames[species][anim] as string);
  }

  private setPetWalkAnimation(pet: PetEntity | null) {
    const species = pet === this.myPet ? this.mySpecies : this.opponentSpecies;
    const animNames = petAnimationNames[
      species
    ] as unknown as AdultAnimationNames;
    pet?.setAnimation(animNames.normal.walking);
  }

  private async createJankenSpines() {
    await spineLoader(JANKEN_SPINE);
    this.jankenSpineBlue = new SpineView({
      superview: this.box,
      spinePath: JANKEN_SPINE,
      x: 225,
      y: 313,
      zIndex: 15,
      scale: JANKEN_SPINE_SCALE,
    });
    this.jankenSpineRed = new SpineView({
      superview: this.box,
      spinePath: JANKEN_SPINE,
      x: 455,
      y: 313,
      zIndex: 15,
      scale: JANKEN_SPINE_SCALE,
    });
  }

  private async initCareSpines() {
    await this.createJankenSpines();
    if (!this.jankenSpineRed || !this.jankenSpineBlue) return;
    this.jankenSpineRed.state.setAnimation(0, 'red_init', false);
    this.jankenSpineRed.state.addAnimation(0, 'red_idle', true, 0);
    this.jankenSpineBlue.state.setAnimation(0, 'blue_init', false);
    this.jankenSpineBlue.state.addAnimation(0, 'blue_idle', true, 0);
  }

  /** Play the spine countdown for one or both sides, driving the 3/2/1 number overlay. */
  private async playSpineCountdown(sides: 'blue' | 'both') {
    if (!this.jankenSpineBlue) return;
    const blue = this.jankenSpineBlue;
    const red = sides === 'both' ? this.jankenSpineRed : null;

    // Switch to play animation
    blue.state.setAnimation(0, 'blue_play', true);
    if (red) red.state.setAnimation(0, 'red_play', true);

    void this.playCountdownOverlay();
    void this.playCountdownSfx();

    // Wait for the play animation duration independently from countdown overlay
    await waitForItPromise(JANKEN_COUNTDOWN_TIMING.playDuration);
  }

  /** Play countdown SFX independently from the visual overlay. */
  private async playCountdownSfx() {
    const { sfxCount, sfxVolume, playDuration } = JANKEN_COUNTDOWN_TIMING;
    const interval = playDuration / sfxCount;
    for (let i = 0; i < sfxCount; i++) {
      sounds.playSound('progressCounted', sfxVolume);
      await waitForItPromise(interval);
    }
  }

  /** Animate the 3/2/1 countdown number overlay. */
  private async playCountdownOverlay() {
    this.countdownImage.updateOpts({ visible: true, opacity: 0, scale: 0.5 });

    for (const [
      image,
      delayBefore,
      holdDuration,
    ] of JANKEN_COUNTDOWN_TIMING.numbers) {
      await waitForItPromise(delayBefore);
      this.countdownImage.setImage(image);
      await gsap.to(this.countdownImage.style, {
        opacity: 1,
        scale: 1,
        duration: 0.1,
        ease: 'back.out(1.7)',
      });
      await waitForItPromise(holdDuration);
      await gsap.to(this.countdownImage.style, {
        opacity: 0,
        scale: 0.5,
        duration: 0.1,
      });
    }

    this.countdownImage.updateOpts({ visible: false });
  }

  /**
   * Play the full result reveal sequence on janken spines:
   * 1. Set skins (hand signs)
   * 2. red_result / blue_result — transition from countdown to sign reveal
   * 3. red_result_win|lose / blue_result_win|lose — outcome animation
   * 4. red_result_win|lose_idle / blue_result_win|lose_idle — final loop
   */
  private async applyJankenSpineResult(
    myMove: JankenMove,
    opponentMove: JankenMove,
    myOutcome: JankenOutcome,
    sides: 'blue' | 'both',
  ) {
    if (!this.jankenSpineBlue) return;
    const blue = this.jankenSpineBlue;
    const red = sides === 'both' ? this.jankenSpineRed : null;

    const blueOutcome =
      myOutcome === 'win' || myOutcome === 'draw'
        ? 'blue_result_win'
        : 'blue_result_lose';
    const redOutcome =
      myOutcome === 'lose' || myOutcome === 'draw'
        ? 'red_result_win'
        : 'red_result_lose';

    // Set skins so the correct hand sign is visible in all result animations
    blue.skeleton.setSkinByName(myMove);
    if (red) red.skeleton.setSkinByName(opponentMove);

    // Transition from countdown → sign reveal
    blue.state.setAnimation(0, 'blue_result', false);
    if (red) red.state.setAnimation(0, 'red_result', false);
    await waitForItPromise(JANKEN_RESULT_TIMING.resultReveal);

    // Outcome animation (draw: both play win)
    sounds.playSound(OUTCOME_SFX[myOutcome], 0.5);
    blue.state.setAnimation(0, blueOutcome, false);
    if (red) red.state.setAnimation(0, redOutcome, false);
    await waitForItPromise(JANKEN_RESULT_TIMING.winLose);

    // Final idle loop
    blue.state.setAnimation(0, blueOutcome + '_idle', true);
    if (red) red.state.setAnimation(0, redOutcome + '_idle', true);
  }

  private applyOutcomeAnimations(myOutcome: JankenOutcome) {
    if (myOutcome === 'win') {
      this.setPetAnimation(this.myPet, 'joy');
      this.setPetAnimation(this.opponentPet, pickRandom(['cry', 'angry']));
    } else if (myOutcome === 'lose') {
      this.setPetAnimation(this.myPet, pickRandom(['cry', 'angry']));
      this.setPetAnimation(this.opponentPet, 'joy');
    } else {
      this.setPetAnimation(this.myPet, 'surprised');
      this.setPetAnimation(this.opponentPet, 'surprised');
    }
  }

  private async showOutcomeImage(outcome: JankenOutcome) {
    const locale = StateObserver.getLocale() === 'ja' ? 'ja' : 'en';
    const { image, width, height } = OUTCOME_IMAGES[outcome][locale];
    this.outcomeImage.setImage(image);
    const scaledWidth = width * 0.7;
    const scaledHeight = height * 0.7;
    this.outcomeImage.updateOpts({
      visible: true,
      opacity: 0,
      scale: 0.3,
      centerAnchor: true,
      width: scaledWidth,
      height: scaledHeight,
      x: (680 - scaledWidth) / 2,
      y: 490 - scaledHeight / 2,
    });

    // Pop in
    await gsap.to(this.outcomeImage.style, {
      opacity: 1,
      scale: 1,
      duration: 0.4,
      ease: 'back.out(1.7)',
    });

    // Hold
    await waitForItPromise(JANKEN_RESULT_TIMING.outcomeImageHold);

    // Pop out
    await gsap.to(this.outcomeImage.style, {
      opacity: 0,
      scale: 0.3,
      duration: 0.3,
      ease: 'back.in(1.7)',
    });

    this.outcomeImage.updateOpts({ visible: false });
  }

  // ---------------------------------------------------------------------------
  // Care mode — two pets, ?, timer, care CTA
  // ---------------------------------------------------------------------------

  private initCare(args: PopupJankenCareArgs) {
    this.currentCloseAction = 'dismissed';
    this.inCareMode = true;
    this.onCtaClick = null;
    this.mySpecies = args.mySpecies ?? 'dog';
    this.opponentSpecies = args.opponentSpecies;

    // Spine circles — question marks with entrance animation
    void this.initCareSpines();

    // Timer
    this.startTimer(args.expiresAt);

    // Outcome image — hidden
    this.outcomeImage.updateOpts({ visible: false });

    // Instruction — care text
    this.instructionBg.updateOpts({ visible: true, opacity: 0.7 });
    this.instructionText.updateOpts({ visible: true });
    this.instructionText.localeText = () => i18n('janken.careInstruction');

    // CTA hidden, care button visible with current love value
    this.ctaButton.updateOpts({ opacity: 0 });
    this.careButton.updateOpts({ visible: true, opacity: 1 });
    const { max } = attributeBounds;
    const state = StateObserver.getState();
    const love = state.pet[state.ui.visibleStats]?.attributes.love ?? 0;
    this.careButton.setLoveValue(love / max);

    // Both pets — bigger in care mode (+0.2)
    this.myPet = createUIPet('happy', this.mySpecies);
    this.myPet.updateOpts({
      superview: this.box,
      x: 171,
      y: 481,
      scale: 0.85,
    });

    this.opponentPet = createUIPet('happy', this.opponentSpecies);
    this.opponentPet.setFlipped(true);
    this.opponentPet.updateOpts({
      superview: this.box,
      x: 471,
      y: 483,
      scale: 0.85,
    });

    // Load opponent pet assets with spinner — visible immediately
    void withLoading(() => petSpeciesAssets[this.opponentSpecies].load(), {
      superview: this.opponentPet,
      scale: 1.25,
      feature: 'pet',
      subFeature: 'pet_assets',
    });

    // Name labels
    this.showNameLabels(args.opponentName);
  }

  // ---------------------------------------------------------------------------
  // Care → Result in-place transition
  // ---------------------------------------------------------------------------

  public awaitCareTap(): Promise<void> {
    this.careTapPromise = new NakedPromise();
    return this.careTapPromise;
  }

  public awaitResultComplete(): Promise<void> {
    return this.resultCompletePromise ?? Promise.resolve();
  }

  public async switchToResult(args: {
    myOutcome: JankenOutcome;
    myMove: JankenMove;
    opponentMove: JankenMove;
    hasReward: boolean;
    myName: string;
    opponentName: string;
  }) {
    this.killAnimations();
    this.inCareMode = false;
    this.currentCloseAction = getResultCloseAction(
      args.myOutcome,
      args.hasReward,
    );

    // Hide care elements
    await gsap.to(this.careButton.style, { opacity: 0, duration: 0.3 });
    this.careButton.updateOpts({ visible: false, opacity: 1 });

    // Hide timer and instruction area
    this.stopTimer();
    this.timerContainer.updateOpts({ visible: false });
    gsap.to(this.instructionBg.style, { opacity: 0, duration: 0.3 });
    await gsap.to(this.instructionText.style, { opacity: 0, duration: 0.3 });
    this.instructionBg.updateOpts({ visible: false });
    this.instructionText.updateOpts({ visible: false });

    // CTA — hidden, postResultSequence will configure and fade it in
    this.ctaButton.updateOpts({ opacity: 0 });

    // Run the result animation (countdown → moves → outcome → CTA)
    await this.animateResult(
      args.myMove,
      args.opponentMove,
      args.myOutcome,
      args.opponentName,
      args.hasReward,
    );
  }

  // ---------------------------------------------------------------------------
  // Name label helpers
  // ---------------------------------------------------------------------------

  private showNameLabels(opponentName: string) {
    this.myNameLabel.updateOpts({ visible: true });
    this.opponentNameLabel.updateOpts({ visible: true });
    this.opponentNameText.localeText = () => opponentName;
  }

  // ---------------------------------------------------------------------------
  // Lifecycle overrides
  // ---------------------------------------------------------------------------

  public fadeOut() {
    this.careTapPromise = null;
    this.resultCompletePromise = null;
    this.cleanupPets();
    instance = null;
    super.fadeOut();
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  private startTimer(expiresAt: number) {
    this.expiresAt = expiresAt;
    this.stopTimer();
    this.timerContainer.updateOpts({ visible: true });
    this.updateTimerText();
    this.timerInterval = createIntervalEmitter(
      (_state, currentNow) => ({ now: currentNow }),
      1000,
    );
    this.timerInterval.addListener(() => this.updateTimerText());
  }

  private updateTimerText() {
    const remaining = Math.max(0, this.expiresAt - now());
    this.timerText.localeText = () =>
      remaining > 0 ? formatDuration(remaining) : i18n('janken.expired');
  }

  private stopTimer() {
    this.timerInterval?.removeAllListeners();
    this.timerInterval = null;
  }

  private cleanupPets() {
    this.killAnimations();
    this.stopTimer();
    this.inCareMode = false;
    this.onCtaClick = null;
    this.myPet?.removeFromSuperview();
    this.opponentPet?.removeFromSuperview();
    this.myPet = null;
    this.opponentPet = null;
    this.careButton.updateOpts({ visible: false });
    this.rerollButton.updateOpts({ visible: false });
    this.shareButtons.reset();
    this.ctaButton.updateOpts({ visible: true, x: 340 });
    this.myNameLabel.updateOpts({ visible: false });
    this.opponentNameLabel.updateOpts({ visible: false });
    this.jankenSpineRed?.removeFromSuperview();
    this.jankenSpineBlue?.removeFromSuperview();
    this.smokeSpine?.removeFromSuperview();
    this.jankenSpineRed = null;
    this.jankenSpineBlue = null;
    this.smokeSpine = null;
  }

  private killAnimations() {
    gsap.killTweensOf(this.outcomeImage.style);
    gsap.killTweensOf(this.ctaButton.style);
    this.shareButtons.killAnimations();
    gsap.killTweensOf(this.rerollButton.style);
    gsap.killTweensOf(this.countdownImage.style);
    gsap.killTweensOf(this.careButton.style);
    gsap.killTweensOf(this.instructionBg.style);
    gsap.killTweensOf(this.instructionText.style);
    if (this.myPet) gsap.killTweensOf(this.myPet.style);
    if (this.opponentPet) gsap.killTweensOf(this.opponentPet.style);
    this.jankenSpineRed?.state.clearTracks();
    this.jankenSpineBlue?.state.clearTracks();
    this.smokeSpine?.state.clearTracks();
  }
}
